// ========== GROCERY RECEIPT COMPONENT ==========
// Displays transaction receipt with download as PNG functionality
import React, { useRef } from 'react';
import { Modal, Button, Typography, Divider, Space, Tag } from 'antd';
import { DownloadOutlined, CheckCircleOutlined } from '@ant-design/icons';
// Dynamic import for html2canvas to avoid Vercel build issues

const { Title, Text } = Typography;

const GroceryReceipt = ({ open, onClose, receiptData }) => {
  const receiptRef = useRef(null);

  // Download receipt as PNG
  const handleDownload = async () => {
    if (!receiptRef.current) return;
    
    try {
      // Dynamic import to avoid Vercel build issues
      const html2canvas = (await import('html2canvas')).default;
      
      const canvas = await html2canvas(receiptRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        logging: false
      });
      
      const link = document.createElement('a');
      link.download = `receipt-${receiptData?.transactionId || Date.now()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('Download error:', error);
    }
  };

  if (!receiptData) return null;

  const {
    transactionId,
    date,
    items,
    subtotal,
    discount,
    total,
    facilityName,
    cashierName
  } = receiptData;

  return (
    <Modal
      title={<Space><CheckCircleOutlined style={{ color: 'var(--success-color)' }} /> Transaction Complete</Space>}
      open={open}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
        <Button
          key="download"
          type="primary"
          icon={<DownloadOutlined />}
          onClick={handleDownload}
        >
          Download Receipt
        </Button>
      ]}
      width={500}
    >
      <div ref={receiptRef} style={{ padding: '24px', background: '#fff' }}>
        {/* Store Header */}
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <Title level={3} style={{ margin: 0, color: '#000' }}>
            {facilityName || 'CommBook Store'}
          </Title>
          <Text type="secondary" style={{ fontSize: '12px', color: '#666' }}>
            Thank you for shopping with us!
          </Text>
        </div>

        <Divider style={{ margin: '16px 0' }} />

        {/* Transaction Info */}
        <Space direction="vertical" size="small" style={{ width: '100%', marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Text style={{ fontSize: '12px', color: '#666' }}>Transaction ID:</Text>
            <Text strong style={{ fontSize: '12px', color: '#000' }}>{transactionId}</Text>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Text style={{ fontSize: '12px', color: '#666' }}>Date:</Text>
            <Text style={{ fontSize: '12px', color: '#000' }}>
              {date ? new Date(date).toLocaleString() : new Date().toLocaleString()}
            </Text>
          </div>
          {cashierName && (
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text style={{ fontSize: '12px', color: '#666' }}>Cashier:</Text>
              <Text style={{ fontSize: '12px', color: '#000' }}>{cashierName}</Text>
            </div>
          )}
        </Space>

        <Divider style={{ margin: '16px 0' }} />

        {/* Items List */}
        <div style={{ marginBottom: '16px' }}>
          <Text strong style={{ fontSize: '14px', color: '#000' }}>Items</Text>
          <div style={{ marginTop: '8px' }}>
            {items && items.map((item, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '6px 0',
                  borderBottom: index < items.length - 1 ? '1px dashed #f0f0f0' : 'none'
                }}
              >
                <div style={{ flex: 1 }}>
                  <Text style={{ fontSize: '13px', color: '#000' }}>
                    {item.name} <Text type="secondary" style={{ color: '#666' }}>x{item.quantity}</Text>
                  </Text>
                </div>
                <Text style={{ fontSize: '13px', color: '#000' }}>
                  ${((item.price || 0) * (item.quantity || 0)).toFixed(2)}
                </Text>
              </div>
            ))}
          </div>
        </div>

        <Divider style={{ margin: '16px 0' }} />

        {/* Totals */}
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Text style={{ fontSize: '14px', color: '#666' }}>Subtotal:</Text>
            <Text style={{ fontSize: '14px', color: '#000' }}>${(subtotal || 0).toFixed(2)}</Text>
          </div>
          {discount > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text style={{ fontSize: '14px', color: '#666' }}>Discount:</Text>
              <Text style={{ fontSize: '14px', color: 'var(--error-color)' }}>
                -${(discount || 0).toFixed(2)}
              </Text>
            </div>
          )}
          <Divider style={{ margin: '8px 0' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Text strong style={{ fontSize: '18px', color: '#000' }}>Total:</Text>
            <Text strong style={{ fontSize: '18px', color: 'var(--success-color)' }}>
              ${(total || 0).toFixed(2)}
            </Text>
          </div>
        </Space>

        <Divider style={{ margin: '16px 0' }} />

        {/* Footer */}
        <div style={{ textAlign: 'center', marginTop: '24px' }}>
          <Tag color="success">PAID</Tag>
          <div style={{ marginTop: '8px' }}>
            <Text style={{ fontSize: '11px', color: '#999' }}>
              This is a digital receipt. Keep for your records.
            </Text>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default GroceryReceipt;
// ========== GROCERY RECEIPT COMPONENT ENDS ==========
